import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ArrowRight, MessageSquare, Users, ShieldAlert, Newspaper, Calendar, Signal, Map as MapIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, SITE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const HERO_IMG = "https://images.unsplash.com/photo-1601042879364-f3947d3f9c16?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY2l0eSUyMHNreWxpbmUlMjBuaWdodCUyMG5lb24lMjB1cmJhbiUyMHN0cmVldHxlbnwwfHx8fDE3ODg5NzIxMDB8MA&ixlib=rb-4.1.0&q=85";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function HomePage() {
  const [copied, setCopied] = useState(false);

  const { data: news = [] } = useQuery({
    queryKey: ["news"],
    queryFn: () => api.get("/news").then((r) => r.data),
  });
  const { data: recent = [] } = useQuery({
    queryKey: ["forum-recent"],
    queryFn: () => api.get("/forum/recent?limit=5").then((r) => r.data),
  });

  const { data: status } = useQuery({
    queryKey: ["server-status"],
    queryFn: () => api.get("/server/status").then((r) => r.data),
    refetchInterval: 20000,
  });

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(SITE.serverIp);
      setCopied(true);
      toast.success("IP copiée dans le presse-papier !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier l'IP");
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/40 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-6">
            <Badge className="bg-primary/10 text-primary border border-primary/30 font-mono-tech uppercase tracking-widest">
              Garry's Mod · DarkRP · FR
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-balance leading-[0.95]">
              Votre histoire commence à{" "}
              <span className="text-primary">RiverSide</span>.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Plongez dans un univers urbain immersif où chaque rue raconte une histoire.
              Incarnez un flic, un braqueur, un maire ou un simple citoyen — c'est vous qui écrivez la suite.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild size="lg" className="orange-glow font-display uppercase tracking-wider" data-testid="join-server-button">
                <a href={SITE.connectUrl}>Rejoindre le serveur <ArrowRight className="h-4 w-4 ml-2" /></a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2" data-testid="hero-discord-button">
                <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Rejoindre le Discord</a>
              </Button>
            </div>

            {/* IP Copy card */}
            <div className="mt-4 inline-flex items-center gap-3 bg-card border border-border rounded-lg p-2 pl-4">
              <span className="text-xs text-muted-foreground font-mono-tech uppercase">IP</span>
              <code className="font-mono-tech text-primary" data-testid="server-ip-value">{SITE.serverIp}</code>
              <Button
                size="sm"
                variant={copied ? "secondary" : "default"}
                onClick={copyIp}
                data-testid="copy-ip-button"
                className="ml-2"
              >
                {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copié</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copier</>}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <img
                src={SITE.logoUrl}
                alt="RiverSide"
                className="w-full max-w-md ml-auto drop-shadow-[0_10px_40px_rgba(210,84,0,0.35)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Server status */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Card className={`border-2 ${status?.online ? "border-primary/50" : "border-border/70"}`}>
          <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                  {status?.online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-70"/>}
                  <span className={`relative inline-flex rounded-full h-3 w-3 ${status?.online ? "bg-primary" : "bg-muted-foreground"}`}/>
                </span>
                <div>
                  <p className="font-display uppercase text-sm tracking-widest">État du serveur</p>
                  <p className="text-xs text-muted-foreground" data-testid="server-status-value">
                    {status?.online
                      ? <span className="text-primary font-medium">En ligne</span>
                      : status
                      ? "Serveur injoignable — réessai automatique dans 20 s."
                      : "Vérification en cours..."}
                  </p>
                </div>
              </div>
              {status?.online && (
                <>
                  <div className="hidden md:flex items-center gap-2 pl-4 border-l border-border">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="font-mono-tech text-sm" data-testid="server-players-value">
                      {status.player_count}/{status.max_players}
                    </span>
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">joueurs</span>
                  </div>
                  <div className="hidden md:flex items-center gap-2 pl-4 border-l border-border">
                    <MapIcon className="h-4 w-4 text-primary" />
                    <span className="font-mono-tech text-sm truncate max-w-[220px]" data-testid="server-map-value">
                      {status.map_name}
                    </span>
                  </div>
                  {status.ping_ms !== null && status.ping_ms !== undefined && (
                    <div className="hidden lg:flex items-center gap-2 pl-4 border-l border-border">
                      <Signal className="h-4 w-4 text-primary" />
                      <span className="font-mono-tech text-sm">{status.ping_ms} ms</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <Button asChild variant="outline" size="sm">
              <a href={SITE.connectUrl} data-testid="status-join-button">Rejoindre le serveur</a>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Experience section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl mb-10">
          <p className="text-primary font-mono-tech text-xs uppercase tracking-widest mb-3">Le monde de RiverSide</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Une expérience DarkRP moderne</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Incarnez un rôle", text: "Des dizaines de métiers, du policier au braqueur, du maire au journaliste." },
            { icon: MessageSquare, title: "Roleplay semi-sérieux", text: "Un cadre RP structuré, un staff actif et un règlement clair, sans être étouffant." },
            { icon: ShieldAlert, title: "Anti-abus", text: "Modération réactive, protection anti-metagame et gestion transparente des sanctions." },
          ].map((f, i) => (
            <Card key={i} className="group border-border hover:border-primary/60 transition-colors">
              <CardContent className="p-6 space-y-3">
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl uppercase tracking-tight">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* News & Recent forum */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight flex items-center gap-2"><Newspaper className="h-6 w-6 text-primary" /> Actualités & événements</h2>
          </div>
          {news.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Aucune actualité pour le moment.</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {news.map((n) => (
                <Card key={n.id} className="border-border hover:border-primary/60 transition-colors" data-testid={`news-item-${n.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant={n.kind === "event" ? "default" : "secondary"} className="uppercase text-[10px] tracking-widest">
                        {n.kind === "event" ? <><Calendar className="h-3 w-3 mr-1" /> Événement</> : "Actualité"}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono-tech">{formatDate(n.published_at)}</span>
                    </div>
                    <h3 className="font-display text-xl mb-1">{n.title}</h3>
                    <p className="text-sm text-muted-foreground">{n.excerpt}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight mb-6 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> Forum récent</h2>
          {recent.length === 0 ? (
            <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Aucune activité pour le moment.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {recent.map((t) => (
                <Link key={t.id} to={`/forum/t/${t.id}`} data-testid={`recent-topic-${t.id}`}>
                  <Card className="hover:border-primary/60 transition-colors">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium line-clamp-1">{t.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        par {t.author.persona_name} · {t.reply_count} réponses
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          <Button asChild variant="ghost" className="mt-4 w-full">
            <Link to="/forum">Voir tout le forum <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
      </section>

      {/* Quick access */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/reglement" data-testid="quick-reglement">
            <Card className="h-full hover:border-primary/60 transition-colors">
              <CardContent className="p-6">
                <p className="font-mono-tech text-xs uppercase tracking-widest text-primary mb-2">01</p>
                <h3 className="font-display text-2xl uppercase">Règlement</h3>
                <p className="text-sm text-muted-foreground mt-2">Consultez les règles du serveur avant de jouer.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/forum" data-testid="quick-forum">
            <Card className="h-full hover:border-primary/60 transition-colors">
              <CardContent className="p-6">
                <p className="font-mono-tech text-xs uppercase tracking-widest text-primary mb-2">02</p>
                <h3 className="font-display text-2xl uppercase">Forum</h3>
                <p className="text-sm text-muted-foreground mt-2">Suggestions, candidatures, support et discussions.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/boutique" data-testid="quick-boutique">
            <Card className="h-full hover:border-primary/60 transition-colors">
              <CardContent className="p-6">
                <p className="font-mono-tech text-xs uppercase tracking-widest text-primary mb-2">03</p>
                <h3 className="font-display text-2xl uppercase">Boutique</h3>
                <p className="text-sm text-muted-foreground mt-2">Soutenez le serveur et débloquez des avantages en jeu.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
